import { ReactNode } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { ResponsiveStyleValue } from '@mui/system';
import { alpha, useTheme } from '@mui/material/styles';
import { AnimateSharedLayout, motion } from 'framer-motion';
import { RADIUS, REDUCED_MOTION } from 'components/ui/tokens';
import { focusRingSx } from 'components/ui/styleKit';
import { spring, useTourMotion } from 'sections/welcome-tour/motion';

const DEFAULT_TILE_HEIGHT_PX = 38;

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Accent of the raised pill while this option is chosen. */
  tone?: 'primary' | 'success' | 'error';
}

interface SegmentedControlProps<T extends string> {
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  /** Unique per control on screen; the pill glides between options sharing it. */
  layoutId: string;
  /** Grid columns; one equal column per option by default. */
  columns?: ResponsiveStyleValue<string>;
  tileHeight?: number;
}

/** Segmented tabs: the raised pill glides to the chosen option. */
const SegmentedControl = <T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  layoutId,
  columns,
  tileHeight = DEFAULT_TILE_HEIGHT_PX
}: SegmentedControlProps<T>) => {
  const theme = useTheme();
  const { reduce } = useTourMotion();
  const dark = theme.palette.mode === 'dark';

  return (
    <AnimateSharedLayout>
      <Box
        role="radiogroup"
        aria-label={ariaLabel}
        sx={{
          display: 'grid',
          gridTemplateColumns: columns ?? `repeat(${options.length}, 1fr)`,
          gap: 0.5,
          p: 0.5,
          borderRadius: RADIUS.control,
          backgroundColor: alpha(theme.palette.text.primary, dark ? 0.06 : 0.04),
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        {options.map((option) => {
          const active = option.value === value;
          const accent = theme.palette[option.tone ?? 'primary'].main;
          return (
            <ButtonBase
              key={option.value}
              role="radio"
              aria-checked={active}
              onClick={() => onChange(option.value)}
              sx={{
                ...focusRingSx(theme),
                position: 'relative',
                height: tileHeight,
                borderRadius: RADIUS.tile,
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                color: active ? 'text.primary' : 'text.secondary',
                transition: 'color .25s ease',
                '&:hover': { color: 'text.primary' },
                [REDUCED_MOTION]: { transition: 'none' }
              }}
            >
              {active && (
                <Box
                  component={motion.span}
                  layoutId={layoutId}
                  transition={reduce ? { duration: 0 } : spring}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: RADIUS.tile,
                    backgroundColor: 'background.paper',
                    boxShadow: `0 1px 2px ${alpha('#000', dark ? 0.45 : 0.1)}, 0 6px 16px -10px ${alpha(accent, 0.7)}, 0 0 0 1px ${alpha(
                      accent,
                      0.4
                    )}`
                  }}
                />
              )}
              <Box component="span" sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                {option.label}
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
    </AnimateSharedLayout>
  );
};

export default SegmentedControl;
